import React, { useMemo, useRef, useState } from "react";
import { glyphPaths, GLYPH_STROKE_WIDTH } from "./glyphs";
import {
  boundsOf,
  depthSort,
  GLYPH_LIFT,
  GLYPH_SIZE,
  project,
  routeLink,
  TILE_DEPTH,
  TILE_HH,
  TILE_HW,
  tileLeftFace,
  tilePoints,
  tileRightFace,
} from "../../../support/dataImport/topology/iso";
import {
  TOPO_TIERS,
  TOPO_EDGE_KIND_META,
  topoTierCaption,
  topoTierLabel,
  type TopoModel,
  type TopoNode,
  type TopoNodeKind,
  type TopoTierId,
} from "../../../support/dataImport/topology/topologyTypes";

// Read-only isometric renderer for a TopoModel. Ported from the example app's
// modules/topology/components/IsometricCanvas.tsx, with the editing affordances
// (move / connect / add / delete) dropped — pan and zoom only. The accent
// palette (originally `@/modules/ui/palette`) and the glyph <Paths> renderer
// (originally `icons.tsx`) are inlined so this file is self-contained.

// ---------- inlined accent palette (from modules/ui/palette.ts) ----------
type Accent = { name: string; color: string; soft: string };

const ACCENT_CYCLE: Accent[] = [
  { name: "blue", color: "var(--cisco-blue)", soft: "color-mix(in srgb, var(--cisco-blue) 16%, transparent)" },
  { name: "purple", color: "var(--chart-purple)", soft: "color-mix(in srgb, var(--chart-purple) 20%, transparent)" },
  { name: "magenta", color: "var(--magenta)", soft: "color-mix(in srgb, var(--magenta) 16%, transparent)" },
  { name: "green", color: "var(--chart-dark-green)", soft: "color-mix(in srgb, var(--chart-dark-green) 20%, transparent)" },
  { name: "orange", color: "var(--orange)", soft: "color-mix(in srgb, var(--orange) 18%, transparent)" },
  { name: "lightblue", color: "var(--chart-light-blue)", soft: "color-mix(in srgb, var(--chart-light-blue) 18%, transparent)" },
  { name: "amber", color: "var(--chart-amber)", soft: "color-mix(in srgb, var(--chart-amber) 18%, transparent)" },
  { name: "medblue", color: "var(--medium-blue)", soft: "color-mix(in srgb, var(--medium-blue) 18%, transparent)" },
];

function accentAt(index: number): Accent {
  return ACCENT_CYCLE[index % ACCENT_CYCLE.length];
}

const TIER_ACCENT = Object.fromEntries(
  TOPO_TIERS.map((t, i) => [t.id, accentAt(i)]),
) as Record<string, Accent>;

// A caption must clear the tile of the node one band down and one column left.
// At 12px that works out at about fourteen characters; the full name stays in
// the tooltip.
function truncate(label: string, max = 14): string {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

// ---------- inlined glyph <path> renderer (from icons.tsx) ----------
function TopologyGlyphPaths({ kind }: { kind: TopoNodeKind }) {
  return (
    <>
      {glyphPaths(kind).map((p, i) => (
        <path
          key={i}
          d={p.d}
          fill={p.fill ? "currentColor" : "none"}
          stroke={p.fill ? "none" : "currentColor"}
          strokeWidth={p.fill ? undefined : GLYPH_STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={p.opacity}
        />
      ))}
    </>
  );
}

export const IsometricTopology: React.FC<{ model: TopoModel; height?: number }> = ({
  model,
  height = 520,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  const bounds = useMemo(() => boundsOf(model.nodes), [model.nodes]);
  const nodeById = useMemo(() => new Map(model.nodes.map((n) => [n.id, n])), [model.nodes]);
  const ordered = useMemo(() => depthSort(model.nodes), [model.nodes]);

  const grid = useMemo(() => {
    if (model.nodes.length === 0) return { cols: 0, rows: 0 };
    return {
      cols: Math.max(...model.nodes.map((n) => n.col)) + 1,
      rows: Math.max(...model.nodes.map((n) => n.row)) + 1,
    };
  }, [model.nodes]);

  const rowTiers = useMemo(() => {
    const map = new Map<number, TopoTierId>();
    for (const n of model.nodes) if (!map.has(n.row)) map.set(n.row, n.tier);
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [model.nodes]);

  const onPointerMove = (e: React.PointerEvent) => {
    const grab = panRef.current;
    if (grab) {
      setPan({ x: grab.panX + (e.clientX - grab.x), y: grab.panY + (e.clientY - grab.y) });
    }
  };

  const endPointer = () => {
    panRef.current = null;
  };

  const onSurfacePointerDown = (e: React.PointerEvent) => {
    panRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };

  const viewBox = `${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`;
  const cx = bounds.minX + bounds.width / 2;
  const cy = bounds.minY + bounds.height / 2;

  return (
    <div className="relative">
      <svg
        viewBox={viewBox}
        width="100%"
        height={height}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Isometric architecture diagram of the captured estate"
        className="cursor-grab touch-none"
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerLeave={endPointer}
        onPointerDown={onSurfacePointerDown}
      >
        <defs>
          <linearGradient id="topo-floor" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--void-2)" />
            <stop offset="100%" stopColor="var(--midnight-blue)" />
          </linearGradient>
        </defs>
        <rect x={bounds.minX} y={bounds.minY} width={bounds.width} height={bounds.height} fill="url(#topo-floor)" />

        <g transform={`translate(${cx} ${cy}) scale(${zoom}) translate(${-cx + pan.x / zoom} ${-cy + pan.y / zoom})`}>
          <IsoFloor cols={grid.cols} rows={grid.rows} />

          {rowTiers.map(([row, tier]) => {
            const p = project(-1, row);
            const accent = TIER_ACCENT[tier];
            return (
              <text
                key={`tier-${row}`}
                x={p.x + 24}
                y={p.y}
                textAnchor="end"
                fontSize={12}
                fontWeight={600}
                letterSpacing="0.06em"
                fill={accent?.color ?? "var(--cisco-blue)"}
                opacity={0.75}
              >
                {topoTierCaption(tier)}
                <title>{topoTierLabel(tier)}</title>
              </text>
            );
          })}

          <g fill="none" strokeLinecap="round">
            {model.edges.map((edge) => {
              const from = nodeById.get(edge.from);
              const to = nodeById.get(edge.to);
              if (!from || !to) return null;
              const style = TOPO_EDGE_KIND_META[edge.kind] ?? TOPO_EDGE_KIND_META.uplink;
              return (
                <path
                  key={edge.id}
                  d={routeLink(from, to)}
                  stroke={style.color}
                  strokeWidth={2.5}
                  strokeDasharray={style.dash}
                  opacity={style.opacity}
                >
                  <title>
                    {from.label} → {to.label} · {style.label}
                  </title>
                </path>
              );
            })}
          </g>

          {ordered.map((node) => (
            <IsoNode key={node.id} node={node} />
          ))}
        </g>
      </svg>

      <div className="absolute bottom-3 right-3 flex flex-col overflow-hidden rounded-md border border-edge bg-surface/90 backdrop-blur">
        <ZoomButton label="Zoom in" onClick={() => setZoom((z) => Math.min(2.4, z * 1.2))}>+</ZoomButton>
        <ZoomButton
          label="Reset view"
          onClick={() => {
            setZoom(1);
            setPan({ x: 0, y: 0 });
          }}
        >
          ⤢
        </ZoomButton>
        <ZoomButton label="Zoom out" onClick={() => setZoom((z) => Math.max(0.45, z / 1.2))}>−</ZoomButton>
      </div>
    </div>
  );
};

function ZoomButton({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="h-8 w-8 text-sm text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
    >
      {children}
    </button>
  );
}

function IsoFloor({ cols, rows }: { cols: number; rows: number }) {
  if (cols === 0 || rows === 0) return null;
  const lines: React.ReactElement[] = [];
  for (let r = -1; r <= rows; r++) {
    const a = project(-1, r);
    const b = project(cols, r);
    lines.push(<line key={`r${r}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />);
  }
  for (let c = -1; c <= cols; c++) {
    const a = project(c, -1);
    const b = project(c, rows);
    lines.push(<line key={`c${c}`} x1={a.x} y1={a.y} x2={b.x} y2={b.y} />);
  }
  return (
    <g stroke="var(--edge)" strokeWidth={1} opacity={0.5}>
      {lines}
    </g>
  );
}

function IsoNode({ node }: { node: TopoNode }) {
  const p = project(node.col, node.row);
  const accent = TIER_ACCENT[node.tier] ?? accentAt(0);
  const glyphY = p.y - GLYPH_LIFT - GLYPH_SIZE / 2;

  return (
    <g aria-label={`${node.label} — ${node.kind}`} style={{ color: accent.color }}>
      {/* Contact shadow, so tiles read as standing on the floor. */}
      <ellipse cx={p.x} cy={p.y + TILE_HH + TILE_DEPTH + 4} rx={TILE_HW * 0.8} ry={TILE_HH * 0.4} fill="rgba(2,6,16,0.55)" />

      <polygon points={tileLeftFace(p)} fill={accent.color} opacity={0.28} />
      <polygon points={tileRightFace(p)} fill={accent.color} opacity={0.16} />
      <polygon
        points={tilePoints(p)}
        fill={accent.soft}
        stroke={node.legacy ? "var(--chart-amber)" : accent.color}
        strokeWidth={node.legacy ? 2.5 : 1.5}
        strokeDasharray={node.legacy ? "5 3" : undefined}
        opacity={0.9}
      />

      {/* Stem from tile to glyph — the "this label belongs to this tile" cue. */}
      <line x1={p.x} y1={p.y} x2={p.x} y2={glyphY + GLYPH_SIZE} stroke={accent.color} strokeWidth={1} opacity={0.45} />

      <g transform={`translate(${p.x - GLYPH_SIZE / 2} ${glyphY}) scale(${GLYPH_SIZE / 24})`}>
        <TopologyGlyphPaths kind={node.kind} />
      </g>

      {node.legacy && (
        <g transform={`translate(${p.x + GLYPH_SIZE / 2 + 2} ${glyphY - 2})`}>
          <circle r={7} fill="var(--chart-amber, #ffc000)" />
          <text textAnchor="middle" y={3.6} fontSize={10} fontWeight={700} fill="var(--midnight-blue)">
            !
          </text>
          <title>{node.label} — this product family is flagged end-of-life in the catalogue</title>
        </g>
      )}

      <text
        x={p.x}
        y={p.y + TILE_HH + TILE_DEPTH + 22}
        textAnchor="middle"
        fontSize={12}
        fontWeight={500}
        fill={node.legacy ? "var(--chart-amber, #ffc000)" : "var(--ink, #dfe8f5)"}
      >
        {truncate(node.label)}
        <title>{node.label}</title>
      </text>
    </g>
  );
}
